import { useEffect, useMemo, useState, type ReactNode, type RefObject } from 'react';
import type { ContextMeaningOrigin, ContextMeaningPublicConfig } from '@orborix/shared-types';
import { describeSpeakError, speakText } from '../../../lib/englishApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import {
  WordPopover,
  useWordSelection,
  makeConversationResolver,
  makeMeaningResolver,
  type MeaningLookup,
  type WordSelection,
} from '../../../components/word-lookup/index.js';
import { fetchContextMeaningConfig } from '../../../lib/contextMeaningApi.js';
import { ContextMeaningSheet, type ContextMeaningOpen } from '../../library/ContextMeaningSheet.js';
import { contextMeaningOpenOf } from '../../library/SheetTap.js';

/** Alto aproximado de la tarjeta del «significado de esa frase» (sólo elige si abre abajo o arriba). */
const MEANING_POPOVER_H = 460;

export interface BubbleWordLookup {
  wordSel: WordSelection;
  /** Popover de la palabra + ficha del significado: va DENTRO del `cardRef`. */
  overlay: ReactNode;
}

/**
 * Tocar una palabra de una burbuja de la charla (la del coach o la del alumno):
 * selección palabra/frase, resolver (`/lookup`, o el «significado de esa
 * frase» si está prendido), audio de la palabra y la ficha del significado.
 * `text` es el texto limpio que se dibuja; `origin` dice de quién es la
 * burbuja (llega al registro de palabras faltantes).
 */
export function useBubbleWordLookup(
  cardRef: RefObject<HTMLElement | null>,
  text: string,
  opts: { origin: ContextMeaningOrigin; sessionId?: string | undefined },
): BubbleWordLookup {
  const { origin, sessionId } = opts;
  const wordList = useMemo(
    () => text.split(/(\s+)/).filter((p) => p !== '' && !/^\s+$/.test(p)),
    [text],
  );
  // Resolver de conversación: pega a `/lookup` (LLM read-through), frase ON.
  // Con el «significado de esa frase» prendido (encargo 49), la palabra suelta
  // pregunta qué significado revisado tiene en esta oración; apagado (o hasta
  // saberlo), el toque es el de hoy.
  const meaningConfig = useContextMeaningConfig();
  const resolver = useMemo(() => {
    const lookup = makeConversationResolver(sessionId);
    return meaningConfig?.enabled
      ? makeMeaningResolver({
          config: meaningConfig,
          origin,
          fallback: lookup,
          ...(sessionId ? { sessionId } : {}),
        })
      : lookup;
  }, [sessionId, meaningConfig, origin]);
  const [meaningSheet, setMeaningSheet] = useState<ContextMeaningOpen | null>(null);
  const wordSel = useWordSelection(cardRef, wordList, text, {
    resolver,
    enablePhrase: true,
    // Una burbuja corta es más angosta que el popover: que se salga a los costados.
    overflowCard: true,
    // La tarjeta «Con ejemplo» (encargo 64) es más alta que la traducción de hoy.
    ...(meaningConfig?.enabled ? { estPopoverHeight: MEANING_POPOVER_H } : {}),
    ...(sessionId ? { sessionId } : {}),
  });
  const wordAudio = useAudioBlob();
  const selectedText = wordSel.isPhrase ? wordSel.phraseText : wordSel.singleWord;

  const overlay = (
    <>
      {wordSel.sel && (
        <WordPopover
          isPhrase={wordSel.isPhrase}
          word={wordSel.singleWord}
          phraseText={wordSel.phraseText}
          phraseWordCount={wordSel.phraseWordCount}
          wordEntry={wordSel.wordEntry}
          wordLoading={wordSel.wordLoading}
          phraseInfo={wordSel.phraseInfo}
          anchor={{ cx: wordSel.sel.cx, top: wordSel.sel.top }}
          flip={wordSel.sel.flip}
          arrowDx={wordSel.sel.arrowDx}
          saved={wordSel.saved}
          playing={wordAudio.state === 'playing'}
          playError={wordAudio.state === 'error' ? describeSpeakError(wordAudio.error) : null}
          {...(meaningConfig?.enabled
            ? {
                onOpenMeaning: (m: MeaningLookup) => {
                  const open = contextMeaningOpenOf(m, wordSel.singleWord, text);
                  if (!open) return;
                  wordSel.closeWord();
                  setMeaningSheet(open);
                },
              }
            : {})}
          sentence={text}
          onConfirmPhrase={wordSel.confirmPhrase}
          onPlay={() =>
            selectedText &&
            void wordAudio.toggle(() => speakText(selectedText, sessionId ? { sessionId } : {}))
          }
          onSave={wordSel.toggleSave}
          onClose={wordSel.closeWord}
        />
      )}
      {meaningSheet && meaningConfig && (
        <ContextMeaningSheet
          portal
          initial={meaningSheet}
          config={meaningConfig}
          usage={{ label: 'En esta charla', node: text }}
          onClose={() => setMeaningSheet(null)}
          {...(sessionId ? { sessionId } : {})}
        />
      )}
    </>
  );

  return { wordSel, overlay };
}

/** La config del «significado de esa frase»: `null` hasta saberla (mientras, el toque de hoy). */
function useContextMeaningConfig(): ContextMeaningPublicConfig | null {
  const [cfg, setCfg] = useState<ContextMeaningPublicConfig | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchContextMeaningConfig().then((c) => {
      if (alive) setCfg(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return cfg;
}
